import React, { useEffect, useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import {
  cancelOutstandingImport,
  closeOutstandingImport,
  commitOutstandingImport,
  downloadOutstandingTemplate,
  fetchOutstandingImport,
  uploadOutstandingSheet,
  type OutstandingKind,
  type OutstandingPushDTO,
  type OutstandingReviewDTO,
} from "../../data/outstandingApi";
import { KIND_COPY, hasBeenPushed } from "../../domain/outstandingImport";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { DownloadIcon, SheetIcon, UploadIcon } from "../../components/icons";
import { cn } from "../../components/cn";
import { OutstandingImportRows } from "./OutstandingImportRows";
import { OutstandingImportSummary } from "./OutstandingImportSummary";

/**
 * Outstanding documents: download the template, fill it, upload it, push it.
 *
 * A migrating client arrives with documents unpaid as of their account start
 * date. The CA downloads an Excel template, lists every unpaid customer
 * invoice — plus a credit-note row for any customer holding an advance — or
 * every unpaid vendor bill plus vendor-credit rows, and uploads it back. We
 * validate against the synced Zoho contact book and stage the rows locally.
 *
 * UPLOAD REACHES NOTHING. It parses, validates and stores; the answer is what
 * WOULD be sent. Only the push writes, only from the confirm below, and it
 * writes real documents — invoices, bills, credit notes — that cannot be
 * unwritten. Full reasoning in docs/onboarding-outstanding-invoices.md.
 *
 * Two sheets, two instances of this panel: kind "invoice" is the customer
 * sheet, kind "bill" the vendor one, and they are independent — a customer
 * sheet under review does not disturb a vendor sheet beside it.
 */

type Phase = "loading" | "idle" | "uploading" | "review";

/**
 * How many batches one press of Push is allowed to run.
 *
 * The loop exists because Zoho has no bulk create and one HTTP request cannot
 * hold a hundred sequential writes. The cap exists because a loop driven by a
 * server field is a loop that hammers the server if that field ever gets stuck
 * — the page stops and offers Continue rather than spinning. Each document
 * costs two Zoho calls (the create, then the status call that makes the draft
 * real), so a batch settles roughly twelve rows; eighty batches is about the
 * parser's own 1000-row ceiling.
 */
const MAX_BATCHES = 80;

export function OutstandingImportPanel({
  clientId,
  clientName,
  organisationName,
  kind,
  onChanged,
}: {
  clientId: ClientId;
  clientName: string;
  /** Named on the push button, because "which books am I writing to" is the question. */
  organisationName?: string | null;
  /** Which sheet this instance is: "invoice" customers, "bill" vendors. */
  kind: OutstandingKind;
  /**
   * Called after any event that changes what is staged or pushed — a successful
   * upload, a discard, each commit response, a close — because the parent
   * recomputes the derived AR/AP totals from it.
   */
  onChanged?: () => void;
}) {
  const copy = KIND_COPY[kind];

  const [phase, setPhase] = useState<Phase>("loading");
  const [review, setReview] = useState<OutstandingReviewDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [push, setPush] = useState<OutstandingPushDTO | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // A push spans several requests. If the panel unmounts part-way — the CA
  // moved to another sub-step — the remaining batches must not keep firing
  // into a screen nobody is watching.
  const liveRef = useRef(true);

  useEffect(() => {
    // Reset wholesale when the kind flips: this is one component rendered once
    // per sheet, and a customer review bleeding into the vendor instance would
    // show the wrong rows over the wrong push button.
    liveRef.current = true;
    setPhase("loading");
    setPush(null);
    setConfirming(false);
    setError(null);

    fetchOutstandingImport(clientId, kind)
      .then((result) => {
        if (!liveRef.current) return;
        setReview(result);
        setPhase(result.import === null ? "idle" : "review");
      })
      .catch(() => {
        // A review we could not load is not worth an error banner: the panel
        // still works, it just starts empty.
        if (liveRef.current) setPhase("idle");
      });

    return () => {
      liveRef.current = false;
    };
  }, [clientId, kind]);

  function download() {
    setBusy(true);
    setError(null);

    downloadOutstandingTemplate(clientId, kind)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not build the sheet.");
      })
      .finally(() => setBusy(false));
  }

  function upload(file: File) {
    setPhase("uploading");
    setError(null);
    setPush(null);
    setConfirming(false);

    uploadOutstandingSheet(clientId, kind, file)
      .then((result) => {
        setReview(result);
        setPhase("review");
        // Staged rows changed, so the derived AR/AP above this panel did too.
        onChanged?.();
      })
      .catch((cause: unknown) => {
        // Every message the server sends here is written for a CA — "row 12
        // names a customer that is not in Zoho yet" — so it goes on screen as
        // it is.
        setError(cause instanceof Error ? cause.message : "Could not read that sheet.");
        setPhase(review?.import ? "review" : "idle");
      })
      .finally(() => {
        // Otherwise picking the same file twice after a fix fires no change event.
        if (inputRef.current) inputRef.current.value = "";
      });
  }

  function discard() {
    setBusy(true);

    cancelOutstandingImport(clientId, kind)
      .then((result) => {
        setReview(result);
        setPhase("idle");
        setError(null);
        setPush(null);
        setConfirming(false);
        // Staged rows changed, so the derived AR/AP above this panel did too.
        onChanged?.();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not discard the import.");
      })
      .finally(() => setBusy(false));
  }

  function close() {
    setBusy(true);

    closeOutstandingImport(clientId, kind)
      .then((result) => {
        setReview(result);
        setPhase("idle");
        setPush(null);
        setError(null);
        // Staged rows changed, so the derived AR/AP above this panel did too.
        onChanged?.();
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not close the import.");
      })
      .finally(() => setBusy(false));
  }

  /**
   * Run the push to completion, or to the first reason to stop.
   *
   * Sequential and deliberately so — each call tells us what is left, and
   * overlapping two would have them both read the same pending rows.
   */
  async function runPush(acknowledgeDuplicate: boolean) {
    setConfirming(false);
    setPushing(true);
    setError(null);

    try {
      // The server's queue only holds rows it can still act on, so
      // "committing" always means real work is left and every batch moves
      // forward. There is no longer a no-progress case for the browser to
      // detect: the loop that used to watch the settled count for a number
      // that stopped moving existed because refused rows were re-offered
      // forever, and they are not any more.

      for (let batch = 0; batch < MAX_BATCHES; batch++) {
        const result = await commitOutstandingImport(clientId, kind, acknowledgeDuplicate);
        if (!liveRef.current) return;

        setReview(result);
        setPush(result.push ?? null);
        // Every batch lands real documents, so the derived AR/AP above move
        // now, not only when the whole push finishes.
        onChanged?.();

        // Halted is not "keep going slower" — it is a rate limit or a refused
        // credential, and the next call would hit the same wall.
        if (!result.push || result.push.status === "done" || result.push.halted) break;
      }
    } catch (cause: unknown) {
      if (!liveRef.current) return;

      setError(cause instanceof Error ? cause.message : "The push stopped.");

      // Whatever went wrong, the truth about which rows made it is on the
      // server, not in whatever we last rendered.
      await fetchOutstandingImport(clientId, kind)
        .then((fresh) => {
          if (liveRef.current) {
            setReview(fresh);
            // A failed batch can still have landed documents before it broke.
            onChanged?.();
          }
        })
        .catch(() => undefined);
    } finally {
      if (liveRef.current) setPushing(false);
    }
  }

  function onDrop(event: React.DragEvent) {
    event.preventDefault();
    setDragging(false);

    const file = event.dataTransfer.files[0];
    if (file) upload(file);
  }

  if (phase === "loading") {
    return (
      <section className="flex flex-col gap-3 rounded-lg border p-4">
        <h3 className="text-base font-medium">{copy.title}</h3>
        <p className="text-sm text-muted-foreground">Loading…</p>
      </section>
    );
  }

  const summary = review?.import ?? null;
  const rows = review?.rows ?? [];
  const pushed = summary !== null && hasBeenPushed(summary);
  const toPush = summary === null ? 0 : summary.new_rows + summary.changed_rows;
  // Same phrasing as the ledger and contact panels, and for the same reason:
  // the organisation name on its own read as an environment rather than as a
  // customer's accounting system.
  const books = `${organisationName ?? clientName}'s Zoho Books`;
  const retryable = summary !== null && summary.status === "committing";

  return (
    <section className="flex flex-col gap-4 rounded-lg border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-medium">{copy.title}</h3>
          <p className="text-sm text-muted-foreground">{copy.description}</p>
        </div>
        <CoButton
          variant="outline"
          onClick={download}
          disabled={busy || pushing || phase === "uploading"}
        >
          <DownloadIcon className="size-4" />
          Download sheet
        </CoButton>
      </div>

      {error !== null && <Callout variant="warning">{error}</Callout>}

      {summary === null ? (
        /*
          A label wrapping a hidden input rather than a button: it gives keyboard
          and screen-reader behaviour for free, which a div with an onClick does
          not.

          `relative` is load-bearing, not styling. The input below is `sr-only`,
          which is `position: absolute`, and without a positioned ancestor it
          resolves against the BODY — escaping the step's scroll container and
          stretching the page. See LedgerImportPanel, where this cost an
          afternoon.
        */
        <label
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={cn(
            "relative flex cursor-pointer flex-col items-center gap-1.5 rounded-md border border-dashed p-6 text-center transition-colors",
            dragging ? "border-primary bg-primary/5" : "hover:bg-muted/40",
            phase === "uploading" && "pointer-events-none opacity-70"
          )}
        >
          <UploadIcon className="size-5 text-muted-foreground" />
          <span className="text-sm font-medium">
            {phase === "uploading"
              ? "Reading the sheet…"
              : "Drop the filled-in sheet here, or click to choose"}
          </span>
          <span className="text-xs text-muted-foreground">.xlsx or .csv, up to 2 MB</span>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.csv"
            className="sr-only"
            disabled={phase === "uploading"}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) upload(file);
            }}
          />
        </label>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <SheetIcon className="size-4 shrink-0 text-muted-foreground" />
            <span className="font-medium">{summary.original_filename}</span>
            <span className="text-xs text-muted-foreground">{summary.total_rows} rows read</span>
          </div>

          <OutstandingImportSummary
            summary={summary}
            rows={rows}
            kind={kind}
            push={push}
            pushing={pushing}
          />
          <OutstandingImportRows rows={rows} kind={kind} showOutcomes={pushed} />

          <div className="flex flex-col gap-3 border-t pt-4">
            {confirming ? (
              <PushConfirm
                count={toPush}
                books={books}
                alreadyPushedAt={summary.already_pushed_at}
                onConfirm={() => void runPush(true)}
                onCancel={() => setConfirming(false)}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                {summary.status === "done" ? (
                  <>
                    <CoButton onClick={close} disabled={busy}>
                      Close
                    </CoButton>
                    {/* Only when there is something to do about it. A number
                        Zoho already held is not a refusal any more — it settles
                        as its own outcome — so a refusal here always does mean
                        the sheet needs a hand. */}
                    {summary.failed_rows > 0 && (
                      <span className="text-xs text-muted-foreground">
                        Fix the refused rows in the sheet and upload it again.
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {/*
                      One click, straight to Zoho — the review above IS the
                      confirmation. The CA uploaded this sheet, read a diff, and
                      is looking at the documents by number and row number. A
                      dialog restating that adds no fact, and a confirm that
                      never says anything new is one people learn to click
                      through, so it stops working on the day it matters.

                      The button names the organisation instead, because that is
                      the one thing the review never shows and the one mistake
                      worth catching: a seat can be reset and re-pointed, and
                      "whose books" is not answerable from a list of invoices.
                    */}
                    <CoButton
                      onClick={() =>
                        summary.already_pushed_at !== null ? setConfirming(true) : void runPush(false)
                      }
                      disabled={pushing || busy || toPush === 0}
                    >
                      {pushing
                        ? "Pushing…"
                        : retryable
                          ? `Continue (${push?.remaining ?? "some"} left)`
                          : `Push ${toPush} to ${books}`}
                    </CoButton>
                    <CoButton variant="outline" onClick={discard} disabled={busy || pushing}>
                      Discard
                    </CoButton>
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * The gate for pushing a file that has already been pushed.
 *
 * NOT a generic "are you sure" — an ordinary push does not come through here.
 * This exists for the one case where the screen knows something the CA
 * demonstrably does not, because if they knew it they would not be pressing the
 * button: this exact spreadsheet has been through already. Softer here than on
 * the contact and ledger sheets — Zoho refuses a repeated document number
 * outright (code 1001), so what already landed bounces back as refused rather
 * than silently doubling — but a re-push the CA does not know is a re-push is
 * still worth a pause. Not blocked, because re-running after a partial failure
 * is a real thing people need to do.
 *
 * Inline rather than a modal: the rows it is talking about are just above, and a
 * dialog would cover them at exactly the moment somebody wants to look again.
 */
function PushConfirm({
  count,
  books,
  alreadyPushedAt,
  onConfirm,
  onCancel,
}: {
  count: number;
  books: string;
  alreadyPushedAt: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-4">
      <p className="text-sm">
        This exact file was already pushed on{" "}
        <span className="font-medium">{alreadyPushedAt}</span>.
      </p>
      <p className="text-sm text-muted-foreground">
        Sending it again re-attempts every document on it against{" "}
        <span className="font-medium">{books}</span>. Zoho refuses a document number it already
        holds, so what landed the first time comes back refused rather than doubled, but anything
        that never made it will be created now. Continue only if you are re-running a push that did
        not finish.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <CoButton onClick={onConfirm}>Push {count} again anyway</CoButton>
        <CoButton variant="outline" onClick={onCancel}>
          Cancel
        </CoButton>
      </div>
    </div>
  );
}
